/**
 * SSE connection state types
 */
export type SSEConnectionState = 'connecting' | 'connected' | 'disconnected' | 'failed'

/**
 * Base stream event structure
 * Actual implementations may have more specific type constraints
 */
export interface BaseStreamEvent {
  type: string
  content: string
  timestamp: string
  data?: Record<string, unknown>
}

/**
 * Fetch SSE ticket function type
 */
export type FetchSseTicketFn = () => Promise<string>

/**
 * SSE client configuration options
 */
export interface SSEClientOptions {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  onStateChange?: (state: SSEConnectionState, retryCount: number) => void
  fetchSseTicket?: FetchSseTicketFn
  /**
   * When provided, called on each connection attempt to produce the base URL.
   * Overrides the static `url` argument passed to the constructor.
   * Useful when the URL must embed dynamic query params (e.g. `?since=<seq>`)
   * that should be re-read fresh on every reconnect.
   */
  urlFactory?: () => string
  /**
   * When true, the connection stays open even when an event with type
   * 'complete' or 'error' arrives. Use this for long-lived global event buses
   * where those types are payload-level events, not connection-level signals.
   */
  persistent?: boolean
}

/**
 * Event handler types
 */
export type SSEEventHandler<T extends BaseStreamEvent = BaseStreamEvent> = (event: T) => void
export type SSEErrorHandler = (error: Error) => void

const DEFAULT_MAX_RETRIES = 10
const DEFAULT_INITIAL_DELAY = 1000 // 1s
const DEFAULT_MAX_DELAY = 30000 // 30s

/**
 * SSEClient - Consolidated SSE client with automatic reconnection and exponential backoff.
 *
 * Features:
 * - Automatic reconnection on connection loss
 * - Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (max)
 * - Single-use SSE ticket handling (fetches new ticket on each reconnection)
 * - Connection state tracking
 * - Configurable retry limits and delays
 *
 * Usage:
 * ```ts
 * const client = new SSEClient<MyEventType>('/api/stream', {
 *   maxRetries: 10,
 *   fetchSseTicket: () => getTicket(),
 *   onStateChange: (state, retryCount) => console.log(state, retryCount)
 * })
 *
 * client.addEventListener((event) => console.log(event))
 * client.addErrorListener((error) => console.error(error))
 * client.connect()
 *
 * // Later...
 * client.disconnect()
 * ```
 */
export class SSEClient<T extends BaseStreamEvent = BaseStreamEvent> {
  private url: string
  private options: Required<Omit<SSEClientOptions, 'fetchSseTicket' | 'urlFactory'>> & { urlFactory?: () => string }
  private eventSource: EventSource | null = null
  private retryTimeout: number | null = null
  private retryCount: number = 0
  private state: SSEConnectionState = 'disconnected'
  private cancelled: boolean = false
  private eventHandlers: Set<SSEEventHandler<T>> = new Set()
  private errorHandlers: Set<SSEErrorHandler> = new Set()
  private fetchTicketFn: FetchSseTicketFn

  constructor(url: string, options: SSEClientOptions = {}) {
    this.url = url
    this.options = {
      maxRetries: options.maxRetries ?? DEFAULT_MAX_RETRIES,
      initialDelay: options.initialDelay ?? DEFAULT_INITIAL_DELAY,
      maxDelay: options.maxDelay ?? DEFAULT_MAX_DELAY,
      onStateChange: options.onStateChange ?? (() => {}),
      urlFactory: options.urlFactory,
      persistent: options.persistent ?? false,
    }

    // Use provided fetchSseTicket or default implementation
    this.fetchTicketFn = options.fetchSseTicket ?? this.defaultFetchSseTicket
  }

  /**
   * Default SSE ticket fetch implementation
   */
  private async defaultFetchSseTicket(): Promise<string> {
    // Dynamic import to avoid circular dependency
    const { fetchSseTicket } = await import('./api')
    return fetchSseTicket()
  }

  /**
   * Get current connection state
   */
  getState(): SSEConnectionState {
    return this.state
  }

  /**
   * Get current retry count
   */
  getRetryCount(): number {
    return this.retryCount
  }

  /**
   * Add an event listener
   */
  addEventListener(handler: SSEEventHandler<T>): void {
    this.eventHandlers.add(handler)
  }

  /**
   * Remove an event listener
   */
  removeEventListener(handler: SSEEventHandler<T>): void {
    this.eventHandlers.delete(handler)
  }

  /**
   * Add an error listener
   */
  addErrorListener(handler: SSEErrorHandler): void {
    this.errorHandlers.add(handler)
  }

  /**
   * Remove an error listener
   */
  removeErrorListener(handler: SSEErrorHandler): void {
    this.errorHandlers.delete(handler)
  }

  /**
   * Start the SSE connection
   */
  connect(): void {
    this.cancelled = false
    this.connectInternal(0)
  }

  /**
   * Disconnect and stop reconnection attempts
   */
  disconnect(): void {
    this.cancelled = true
    this.cleanup()
    this.updateState('disconnected', 0)
  }

  /**
   * Update connection state and notify listeners
   */
  private updateState(state: SSEConnectionState, retryCount: number): void {
    this.state = state
    this.retryCount = retryCount
    this.options.onStateChange(state, retryCount)
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoffDelay(attempt: number): number {
    return Math.min(
      this.options.initialDelay * Math.pow(2, attempt),
      this.options.maxDelay
    )
  }

  /**
   * Cleanup EventSource and retry timeout
   */
  private cleanup(): void {
    if (this.retryTimeout !== null) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  }

  /**
   * Notify all event handlers
   */
  private notifyEventHandlers(event: T): void {
    this.eventHandlers.forEach(handler => {
      try {
        handler(event)
      } catch (error) {
        console.error('Error in SSE event handler:', error)
      }
    })
  }

  /**
   * Notify all error handlers
   */
  private notifyErrorHandlers(error: Error): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error)
      } catch (err) {
        console.error('Error in SSE error handler:', err)
      }
    })
  }

  /**
   * Internal connection logic with retry support
   */
  private async connectInternal(attempt: number): Promise<void> {
    if (this.cancelled || attempt >= this.options.maxRetries) {
      if (attempt >= this.options.maxRetries) {
        const error = new Error(`Connection failed after ${this.options.maxRetries} retries`)
        this.notifyErrorHandlers(error)
        this.updateState('failed', attempt)
      }
      return
    }

    this.cleanup()
    this.updateState('connecting', attempt)

    try {
      // Fetch a new single-use ticket for each connection attempt
      const ticket = await this.fetchTicketFn()

      if (this.cancelled) return

      // Resolve base URL — urlFactory is called fresh on each reconnect so dynamic
      // query params (e.g. ?since=<seq>) are re-read from their current source.
      const baseUrl = this.options.urlFactory ? this.options.urlFactory() : this.url

      // Construct URL with ticket
      const urlWithTicket = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}ticket=${encodeURIComponent(ticket)}`
      const eventSource = new EventSource(urlWithTicket)
      this.eventSource = eventSource

      eventSource.onopen = () => {
        if (!this.cancelled) {
          this.updateState('connected', 0) // Reset retry count on successful connection
        }
      }

      eventSource.onmessage = (e) => {
        try {
          const rawEvent = JSON.parse(e.data)
          // Ensure timestamp is always a string
          const event: T = {
            ...rawEvent,
            timestamp: rawEvent.timestamp || new Date().toISOString(),
          } as T

          this.notifyEventHandlers(event)

          // For non-persistent clients, 'complete' or 'error' signals end-of-stream.
          // Persistent clients (e.g. the global event bus) keep the connection open.
          if (!this.options.persistent && (event.type === 'complete' || event.type === 'error')) {
            this.cancelled = true
            this.cleanup()
            this.updateState('disconnected', 0)
          }
        } catch (error) {
          console.error('Failed to parse SSE event:', error)
          const parseError = error instanceof Error ? error : new Error('Failed to parse event')
          this.notifyErrorHandlers(parseError)
        }
      }

      eventSource.onerror = () => {
        if (this.cancelled) return

        this.cleanup()
        this.updateState('disconnected', attempt)

        // Schedule reconnection with exponential backoff
        const delay = this.calculateBackoffDelay(attempt)
        this.retryTimeout = window.setTimeout(() => {
          this.connectInternal(attempt + 1)
        }, delay)
      }
    } catch (error) {
      if (this.cancelled) return

      console.error('Failed to establish SSE connection:', error)
      const connectionError = error instanceof Error ? error : new Error('SSE ticket error')
      this.notifyErrorHandlers(connectionError)
      this.updateState('disconnected', attempt)

      // Retry with exponential backoff
      const delay = this.calculateBackoffDelay(attempt)
      this.retryTimeout = window.setTimeout(() => {
        this.connectInternal(attempt + 1)
      }, delay)
    }
  }
}
